import { useState, useEffect, useContext } from 'react'
import {
  CCard,
  CCardBody,
  CButton,
  CAlert,
  CSpinner,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCardTitle,
  CCardText,
  CImage,
} from '@coreui/react'
import { ProductConfiguration } from '../../../common/constants'
import { ConfigurationService } from '../../../services/ConfigurationService'
import { StepProps } from '../types'
import { EasyContext } from '../../../components/context/EasyContext'
import { useMsal, useMsalAuthentication } from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'

import EasyDirectoryLogo from '../../../assets/brand/TCF_EasyDirectory_Logo_RGB.png'
import { AppRoleAssignments } from '../../../common/interfaces'

const REQUIRED_APP_ROLES = [
  '9c7a330d-35b3-4aa1-963d-cb2b9f927841',  // Contacts.Read.Shared
  'e1fe6dd8-ba31-4d61-89e7-88639da4683d',  // User.Read
  'b340eb25-3456-403f-be2f-af7a0d370277'   // User.Read.All
];

const hasAllAppRoleAssignments = (appId: string, response: AppRoleAssignments) => {
  const roles = response.value;

  if (ProductConfiguration.EasyDirectory.ApplicationId === appId) {
    return true;
    //return REQUIRED_APP_ROLES.every((roleId) => roles.some((item) => item.appRoleId === roleId))
  }
  return true;
}

const ConfigurationConsentStep: React.FC<StepProps> = ({ onNext, onPrevious }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [consentGranted, setConsentGranted] = useState(false)
  const [validationStatus, setValidationStatus] = useState<
    'idle' | 'checking' | 'success' | 'error'
  >('idle')

  const { accounts } = useMsal();

  const { accessToken } = useContext(EasyContext)
  const configurationService = new ConfigurationService(accessToken)

  const REDIRECTURL = `${ProductConfiguration.EasyDirectory.Admin.RedirectUrl}popup-close.html`

  const appRoleRequest = {
    scopes: ["Application.Read.All"],
    account: accounts[0]
  };

  const { acquireToken, result, error } = useMsalAuthentication(InteractionType.Popup, {
    ...appRoleRequest
  });

  // Initial check on mount only
  useEffect(() => {
    if (result) {
      configurationService
        .CheckAppRoleAssignments(ProductConfiguration.EasyDirectory.Admin.ApplicationId, result.accessToken)
        .then((response) => {
          // if (hasAllAppRoleAssignments(ProductConfiguration.EasyDirectory.Admin.ApplicationId, response)) {
          //   setConsentGranted(true)
          // }
        })
        .catch((error) => {
          // console.error('Error checking initial consent:', error)
          // setConsentGranted(false)
        })
    }
    else {
      acquireToken(InteractionType.Popup).then((authresult) => {
        configurationService
        .CheckAppRoleAssignments(ProductConfiguration.EasyDirectory.Admin.ApplicationId, authresult.accessToken)
        .then((response) => {
          // if (hasAllAppRoleAssignments(ProductConfiguration.EasyDirectory.Admin.ApplicationId, response)) {
          //   setConsentGranted(true)
          // }
        })
        .catch((error) => {
          // console.error('Error checking initial consent:', error)
          // setConsentGranted(false)
        })
      });
    }
  }, [result, error, acquireToken])

  const handleConsentClick = () => {
    const consentUrl = `https://login.microsoftonline.com/common/adminconsent?client_id=${ProductConfiguration.EasyDirectory.Admin.ApplicationId}&redirect_uri=${REDIRECTURL}`
    window.open(consentUrl, 'consent', 'width=600,height=600')
  }

  const handleValidateClick = async () => {
    setValidationStatus('checking')
    setIsLoading(true)

    try {
      const auth = await acquireToken(InteractionType.Popup);

      if (auth) {
        const response = await configurationService.CheckAppRoleAssignments(ProductConfiguration.EasyDirectory.Admin.ApplicationId, auth.accessToken)
        const hasConsent = hasAllAppRoleAssignments(ProductConfiguration.EasyDirectory.Admin.ApplicationId, response)

        if (hasConsent) {
          setConsentGranted(true)
          setValidationStatus('success')
        } else {
          setConsentGranted(false)
          setValidationStatus('error')
        }
      } else {
        setConsentGranted(false)
        setValidationStatus('error')
      }
    } catch (error) {
      console.error('Error validating consent:', error)
      setValidationStatus('error')
      setConsentGranted(false)
    } finally {
      setIsLoading(false)
    }
  }

  const permissionsTable = [
    { permission: 'Contacts.Read.Shared', description: 'Read user and shared contacts' },
    { permission: 'User.Read', description: 'Sign in and read user profile' },
    { permission: 'User.Read.All', description: 'Read all users\' full profiles' }
  ];

  return (
    <>
    <CImage fluid src={EasyDirectoryLogo} alt="Easy Directory" width={370} /><br /><br />
    <CCard>
      <CCardBody>
        <CCardTitle>Grant Azure AD Permissions</CCardTitle>

        <CCardText>
          To enable Easy Directory 2.0 Admin Center functionality, your tenant administrator needs to grant the
          following permissions to the application:
        </CCardText>

        <CTable hover bordered className="mb-4">
          <CTableHead>
            <CTableRow>
              <CTableHeaderCell>Permission</CTableHeaderCell>
              <CTableHeaderCell>Description</CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {permissionsTable.map(({ permission, description }) => (
              <CTableRow key={permission}>
                <CTableDataCell>{permission}</CTableDataCell>
                <CTableDataCell>{description}</CTableDataCell>
              </CTableRow>
            ))}
          </CTableBody>
        </CTable>

        {consentGranted ? (
          <CAlert color="success" className="mb-4">
            All required permissions have been granted
          </CAlert>
        ) : (
          <div className="mb-4 d-flex gap-3">
            <CButton color="warning" onClick={handleConsentClick}>
              Grant Permissions
            </CButton>

            <CButton
              color={validationStatus === 'error' ? 'danger' : 'primary'}
              disabled={isLoading}
              onClick={handleValidateClick}
            >
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  Validating...
                </>
              ) : validationStatus === 'error' ? (
                'Retry Validation'
              ) : (
                'Validate Permissions'
              )}
            </CButton>
          </div>
        )}

        <div className="d-flex justify-content-between">
          <CButton color="light" onClick={onPrevious}>
            Previous
          </CButton>
          <CButton color="primary" onClick={onNext} disabled={!consentGranted}>
            Next
          </CButton>
        </div>
      </CCardBody>
    </CCard>
    </>
  )
}

export default ConfigurationConsentStep
